import { useCallback, useContext, useEffect, useRef } from "react";
import AppContext from "../../context";
import ReactTooltip from "react-tooltip";
import walnut from "../../assets/walnut.webp";
import cedar from "../../assets/cedar.webp";
import maple from "../../assets/maple.webp";
import whiteoak from "../../assets/whiteoak.webp";
import {
  convertFractionStringToObject,
  inchFractionsToCmDecimal,
  stripMaterialTitle,
} from "../../util/helpers";
import { readableColor } from "color2k";
import variables from "../../styles/export.module.scss";

const MaterialButton = ({
  selectedMaterial,
  material,
  labelStyle,
  bottomRequired,
  shouldDisableMaterial,
  handleSetBottomRequired,
  handleSetSelectedBottomType,
  disableBottomRequiredSwitch,
  handleSetSelectedMaterial,
  resolveMaterialButtonID,
}) => {
  const buttonRef = useRef();
  const buttonId = resolveMaterialButtonID(
    stripMaterialTitle(material.node.title)
  );
  const loadInsert = useCallback(() => {
    if (stripMaterialTitle(material.node.title) === "Cedar") {
      handleSetBottomRequired(false);
      disableBottomRequiredSwitch(true);
    } else {
      disableBottomRequiredSwitch(false);
    }
    handleSetSelectedMaterial(material.node);
  }, []);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.addEventListener("loadInsertClick", loadInsert);
    }
    return () => {
      if (buttonRef.current) {
        buttonRef.current.removeEventListener("loadInsertClick", loadInsert);
      }
    };
  }, [buttonRef]);

  return (
    <button
      className={`materials--button ${
        selectedMaterial.id === material.node.id ? "selected" : ""
      } reset-button`}
      style={labelStyle}
      onClick={() => {
        if (stripMaterialTitle(material.node.title) === "Cedar") {
          handleSetBottomRequired(false);
          if (bottomRequired)
            document.getElementById("bottom-required").click();
          disableBottomRequiredSwitch(true);
        } else {
          disableBottomRequiredSwitch(false);
          if (
            selectedMaterial.name === "Cedar" &&
            stripMaterialTitle(material.node.title) !== "Cedar"
          )
            setTimeout(() => {
              document.getElementById("bottom-required").click();
            }, 100);
        }
        handleSetSelectedMaterial(material.node);
      }}
      disabled={shouldDisableMaterial(material)}
      data-tip
      data-for={
        shouldDisableMaterial(material)
          ? `${material.node.id}-error-tip`
          : `${material.node.id}-material-tip`
      }
      id={resolveMaterialButtonID(stripMaterialTitle(material.node.title))}
    >
      <img
        src={
          material.node.featuredImage
            ? material.node.featuredImage.originalSrc
            : null
        }
        alt={
          material.node.featuredImage
            ? material.node.featuredImage.altText
            : "material"
        }
        width="28"
        height="28"
      />
      <span>{stripMaterialTitle(material.node.title)}</span>
    </button>
  );
};

const Materials = ({
  imperial,

  handleSetWidthError,
  handleSetDepthError,
  handleSetHeightError,
}) => {
  const {
    materials,
    selectedMaterial,
    handleSetSelectedMaterial,
    handleSetBottomRequired,
    bottomRequired,
    disableBottomRequiredSwitch,
    handleSetSelectedBottomType,
    showTooltips,
    currentInsertDimensions,
    MAX_HEIGHT_DEFAULT,
    MAX_HEIGHT_BAMBOO,
    MIN_HEIGHT,
    MIN_WIDTH_DEPTH,
    MAX_WIDTH_DEPTH,
    /*     MAX_HEIGHT_CEDAR, */
  } = useContext(AppContext);

  useEffect(() => {
    if (materials) {
      const defaultMaterial = materials.find((material) =>
        material.node.tags.includes("default-material")
      );
      if (defaultMaterial) handleSetSelectedMaterial(defaultMaterial.node);
      else handleSetSelectedMaterial(materials[0].node);
    }
  }, [materials]);

  useEffect(() => {
    if (selectedMaterial) {
      if (imperial) {
        let widthInputInchFull = document.getElementById(
          "outside_width_inch_full"
        );
        let depthInputInchFull = document.getElementById(
          "outside_depth_inch_full"
        );
        let heightInputInchFull = document.getElementById(
          "outside_height_inch_full"
        );
        let widthInputInchFraction = document.getElementById(
          "outside_width_inch_fraction"
        );
        let depthInputInchFraction = document.getElementById(
          "outside_depth_inch_fraction"
        );
        let heightInputInchFraction = document.getElementById(
          "outside_height_inch_fraction"
        );
        let widthInchFull = widthInputInchFull.valueAsNumber;
        let depthInchFull = depthInputInchFull.valueAsNumber;
        let heightInchFull = heightInputInchFull.valueAsNumber;
        let widthInchFraction = convertFractionStringToObject(
          widthInputInchFraction.innerText
        );
        let depthInchFraction = convertFractionStringToObject(
          depthInputInchFraction.innerText
        );
        let heightInchFraction = convertFractionStringToObject(
          heightInputInchFraction.innerText
        );

        let widthValue = parseFloat(
          inchFractionsToCmDecimal({
            integer: widthInchFull,
            fraction: widthInchFraction,
          })
        );
        let depthValue = parseFloat(
          inchFractionsToCmDecimal({
            integer: depthInchFull,
            fraction: depthInchFraction,
          })
        );
        let heightValue = parseFloat(
          inchFractionsToCmDecimal({
            integer: heightInchFull,
            fraction: heightInchFraction,
          })
        );
        switch (selectedMaterial.name) {
          case "Bamboo":
            if (heightValue >= MIN_HEIGHT && heightValue <= MAX_HEIGHT_BAMBOO) {
              handleSetHeightError(false);
            } else handleSetHeightError(true);
            break;
          default:
            if (
              heightValue >= MIN_HEIGHT &&
              heightValue <= MAX_HEIGHT_DEFAULT
            ) {
              {
                handleSetHeightError(false);
              }
            } else handleSetHeightError(true);
            break;
        }
        if (widthValue >= MIN_WIDTH_DEPTH && widthValue <= MAX_WIDTH_DEPTH) {
          handleSetWidthError(false);
        } else handleSetWidthError(true);

        if (depthValue >= MIN_WIDTH_DEPTH && depthValue <= MAX_WIDTH_DEPTH) {
          handleSetDepthError(false);
        } else handleSetDepthError(true);
      } else {
        let widthInput = document.getElementById("outside_width");
        let depthInput = document.getElementById("outside_depth");
        let heightInput = document.getElementById("outside_height");
        let widthValue = widthInput.valueAsNumber;
        let heightValue = heightInput.valueAsNumber;
        let depthValue = depthInput.valueAsNumber;
        switch (selectedMaterial.name) {
          case "Bamboo":
            if (heightValue >= MIN_HEIGHT && heightValue <= MAX_HEIGHT_BAMBOO) {
              handleSetHeightError(false);
            } else handleSetHeightError(true);
            break;
          default:
            if (
              heightValue >= MIN_HEIGHT &&
              heightValue <= MAX_HEIGHT_DEFAULT
            ) {
              {
                handleSetHeightError(false);
              }
            } else handleSetHeightError(true);
            break;
        }
        if (widthValue >= MIN_WIDTH_DEPTH && widthValue <= MAX_WIDTH_DEPTH)
          handleSetWidthError(false);
        else handleSetWidthError(true);

        if (depthValue >= MIN_WIDTH_DEPTH && depthValue <= MAX_WIDTH_DEPTH)
          handleSetDepthError(false);
        else handleSetDepthError(true);
      }
    }
  }, [selectedMaterial]);
  const shouldDisableMaterial = (material) => {
    if (
      selectedMaterial.name === "Bamboo" &&
      stripMaterialTitle(material.node.title) !== "Bamboo" &&
      currentInsertDimensions.height.cm > MAX_HEIGHT_DEFAULT
    ) {
      return true;
    } else return false;
  };

  const resolveMaterialButtonID = (name) => {
    switch (name) {
      case "Maple":
        return "material1";
      case "White Oak":
        return "material2";
      case "Walnut":
        return "material3";
      case "Cedar":
        return "material4";
    }
  };

  const labelStyle = {
    color: readableColor(variables.menuBgColor),
  };

  return (
    <>
      <div className="materials" id="materials">
        <p className="materials--title title is-size-6" style={labelStyle}>
          Materials
        </p>
        <div className="materials__columns is-multiline">
          {materials &&
            selectedMaterial &&
            materials
              .filter((material) => material.node.tags.includes("material"))
              .map((material, index) => (
                <div key={material.node.id}>
                  <MaterialButton
                    selectedMaterial={selectedMaterial}
                    material={material}
                    labelStyle={labelStyle}
                    bottomRequired={bottomRequired}
                    shouldDisableMaterial={shouldDisableMaterial}
                    disableBottomRequiredSwitch={disableBottomRequiredSwitch}
                    handleSetBottomRequired={handleSetBottomRequired}
                    handleSetSelectedBottomType={handleSetSelectedBottomType}
                    handleSetSelectedMaterial={handleSetSelectedMaterial}
                    resolveMaterialButtonID={resolveMaterialButtonID}
                  />
                  {shouldDisableMaterial(material) ? (
                    <ReactTooltip
                      id={`${material.node.id}-error-tip`}
                      place="bottom"
                      effect="solid"
                      className="tooltip"
                    >
                      {`Too high for ${stripMaterialTitle(
                        material.node.title
                      )}!`}
                    </ReactTooltip>
                  ) : (
                    showTooltips && (
                      <ReactTooltip
                        id={`${material.node.id}-material-tip`}
                        place="left"
                        effect="solid"
                        className="tooltip"
                      >
                        <div
                          className="pt-2 pb-2 is-flex is-flex-direction-column"
                          style={{ maxWidth: "200px" }}
                        >
                          {stripMaterialTitle(material.node.title) ===
                            "White Oak" && (
                            <img src={whiteoak} alt="whiteoak" width="200" />
                          )}
                          {stripMaterialTitle(material.node.title) ===
                            "Maple" && (
                            <img src={maple} alt="maple" width="200" />
                          )}
                          {stripMaterialTitle(material.node.title) ===
                            "Cedar" && (
                            <img src={cedar} alt="cedar" width="200" />
                          )}
                          {stripMaterialTitle(material.node.title) ===
                            "Walnut" && (
                            <img src={walnut} alt="walnut" width="200" />
                          )}

                          <p className="mt-2">{material.node.description}</p>
                        </div>
                      </ReactTooltip>
                    )
                  )}
                </div>
              ))}
        </div>
      </div>
      <hr className="horizontal"></hr>
    </>
  );
};

export default Materials;
